import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/category/all', label: t('nav.category') },
    { path: '/about', label: t('nav.about') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome && !scrolled
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-sm'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl whitespace-nowrap ${
              isHome && !scrolled ? 'text-white' : 'text-foreground-950'
            }`}
          >
            韩漫漫画网站
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
                    ? isHome && !scrolled
                      ? 'text-white'
                      : 'text-primary-500'
                    : isHome && !scrolled
                      ? 'text-white/80 hover:text-white'
                      : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className={`relative ${isHome && !scrolled ? 'text-white/70' : 'text-foreground-500'}`}>
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm"></i>
              <input
                type="text"
                placeholder={t('nav.search')}
                className={`w-48 pl-9 pr-3 py-1.5 rounded-full text-sm border outline-none transition-all duration-200 focus:w-60 ${
                  isHome && !scrolled
                    ? 'bg-white/15 border-white/20 text-white placeholder-white/50 focus:bg-white/25'
                    : 'bg-background-100 border-background-200 text-foreground-950 placeholder-foreground-400 focus:border-primary-300'
                }`}
              />
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${
              isHome && !scrolled ? 'text-white' : 'text-foreground-950'
            }`}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobile}
                className={`block text-sm font-medium py-2 whitespace-nowrap ${
                  location.pathname === item.path
                    ? 'text-primary-500'
                    : 'text-foreground-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="relative pt-2">
              <i className="ri-search-line absolute left-3 top-[60%] -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                placeholder={t('nav.search')}
                className="w-full pl-9 pr-3 py-2 rounded-full text-sm border border-background-200 bg-background-100 text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300"
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}